import classNames from "classnames";
import { Dinero } from "dinero.js";
import React, { type JSX } from "react";
import { Image } from "tsi-common-react/src/common/Image";
import { Link } from "tsi-common-react/src/common/Link";
import { BundleGroupTypes } from "tsi-common-react/src/constants";
import {
    IConcreteBundle,
    IProduct,
} from "tsi-common-react/src/models/catalogue.interfaces";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import format from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";
import { c, t } from "ttag";

import { IBasketLineSuggestionProps } from "./BasketLineSuggestion.interfaces";

interface IState {}

// TODO(ssr, LEVEL 7 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 7/10 due to complex pricing calculations, DOM queries for financing plans, multiple helper render methods, and bundle headline logic.
export class BasketLineSuggestionA extends React.Component<
    IBasketLineSuggestionProps,
    IState
> {
    private addToBasket() {
        this.props.addBasketLine(this.props.product.url, 1);
        trackAddToBasketEvent(this.props.product, 1);
    }

    private scrollToFinePrint(event: React.MouseEvent<HTMLElement>) {
        if (event) {
            event.preventDefault();
        }

        const finePrint = document.querySelector("#basket-lines__fine-print");
        finePrint?.scrollIntoView({ behavior: "smooth" });
    }

    private buildImage(product: IProduct) {
        // Use the product image if one exists else try the parent image
        let image = product.images.slice().shift();
        if (!image && product.parent) {
            image = product.parent.images.slice().shift();
        }
        return image ? (
            <Link
                href={product.parent?.link || product.link}
                title={t`Learn more about ${product.title}`}
            >
                <Image
                    alt={product.title}
                    src={image.original}
                    className="basket-line__graphic__image"
                />
            </Link>
        ) : null;
    }

    private buildOptions() {
        const attributes = this.props.product.attributes;
        const optionKeys = attributes.product_options?.value || [];
        if (optionKeys.length <= 0) {
            return null;
        }

        const options = optionKeys.map((key) => {
            const attr = attributes[key];
            if (!attr) {
                return null;
            }
            return (
                <li key={key} className="basket-suggestion__details__option">
                    <span>{attr.name}</span>: <strong>{attr.value}</strong>
                </li>
            );
        });

        return (
            <div className="basket-suggestion__details__options">
                <ul>{options}</ul>
            </div>
        );
    }

    private calcPlanMonths(total: Dinero) {
        const plansHTML: HTMLElement[] = [].slice.call(
            document.querySelectorAll(".financing-plan-table__plan"),
        );
        const availablePlans = plansHTML.filter((plan) => {
            return total.greaterThanOrEqual(
                getDinero(plan.dataset.threshold || ""),
            );
        });
        const bestPlan = availablePlans[availablePlans.length - 1];
        return bestPlan ? parseInt(bestPlan.dataset.length || "", 10) : null;
    }

    private buildAddToBasketButton() {
        let addToCartLabel: string = t`Add to cart`;
        let isPreorder = false;
        if (
            this.props.product.attributes &&
            this.props.product.attributes.preorder &&
            this.props.product.attributes.preorder.value
        ) {
            addToCartLabel = t`Pre-Order`;
            isPreorder = true;
        }

        const buttonClasses = classNames({
            "button": true,
            "button--secondary": isPreorder,
        });

        return (
            <button
                className={buttonClasses}
                onClick={this.addToBasket.bind(this)}
            >
                {addToCartLabel}
            </button>
        );
    }

    private buildHeadline(bundles: IConcreteBundle[]) {
        // TODO(i18n): Wrap hardcoded string in t tagged template: t`FORGETTING SOMETHING?`
        let headline = "FORGETTING SOMETHING?";
        for (const bundle of bundles) {
            const bundle_type = bundle.bundle_group.bundle_type;
            if (
                (bundle_type === BundleGroupTypes.DEFAULT ||
                    bundle_type === BundleGroupTypes.IN_BASKET_ADD_ON) &&
                bundle.bundle_group.headline
            ) {
                headline = bundle.bundle_group.headline;
                break;
            }
        }
        return headline;
    }

    render() {
        const line = this.props.line;
        const lineProduct = line.product;
        const lineRootProduct = lineProduct.parent || lineProduct;

        const product = this.props.product;
        const rootProduct = product.parent || product;
        const onFinePrintClick = (event: React.MouseEvent<HTMLElement>) => {
            this.scrollToFinePrint(event);
        };

        let financing: JSX.Element | null = null;
        if (product.price.enable_per_month_pricing) {
            // Calculate Suggested plan based on (Cart total + Suggestion Product total) / (Highest plan that total qualifies for)
            const basketTotalPrice = getDinero(this.props.basketTotal);
            const extraProductPrice = getDinero(
                this.props.product.price.cosmetic_excl_tax || "0.00",
            );
            const computedTotalPrice = basketTotalPrice.add(extraProductPrice);
            const planMonths = this.calcPlanMonths(computedTotalPrice);
            if (planMonths) {
                const computedMonthlyPrice = getMonthlyPrice(
                    getDinero(
                        this.props.product.price.cosmetic_excl_tax || "0.00",
                    ),
                    planMonths,
                );
                financing = (
                    <p>
                        {/* TODO(i18n): Use jt to keep complete sentence together with interpolated components instead of splitting into fragments */}
                        <button
                            onClick={onFinePrintClick}
                            className="basket-suggestion__financing-button"
                        >
                            or{" "}
                            <span className="basket-suggestion__financing--underline">
                                {format.money(computedMonthlyPrice)}/{t`mo`}.
                            </span>
                            <sup>2</sup> {t`for`}{" "}
                            <span className="basket-suggestion__financing--underline">
                                {c("BasketLineSuggestionA")
                                    .t`${planMonths} months`}
                            </span>
                            <sup>1</sup>
                        </button>
                    </p>
                );
            }
        }
        const bundles = line.bundles;
        const headline = this.buildHeadline(bundles);

        return (
            <div
                role="region"
                // TODO(i18n): Wrap aria-label in t tagged template: aria-label={t`Upsell Carousel`}
                aria-label="Upsell Carousel"
                className="basket-suggestion"
            >
                <div className="basket-suggestion__row basket-suggestion__row--shaded">
                    <h3 dangerouslySetInnerHTML={{ __html: headline }} />
                </div>
                <div className="basket-suggestion__row">
                    <div className="basket-suggestion__graphic">
                        {this.buildImage(product)}
                    </div>
                    <div className="basket-suggestion__details">
                        <h5>{t`BEST SELLER for ${lineRootProduct.title}`}</h5>
                        <h4 className="basket-suggestion__details-title">
                            <Link href={rootProduct.link}>
                                {rootProduct.title}
                            </Link>
                        </h4>
                        {this.buildOptions()}
                    </div>
                    <div className="basket-suggestion__price">
                        <p>{format.money(product.price.cosmetic_excl_tax)}</p>
                        <div className="basket-suggestion__financing">
                            {financing}
                        </div>
                        {this.buildAddToBasketButton()}
                    </div>
                </div>
            </div>
        );
    }
}
